import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { insertRow, listRows } from "@/lib/adminApi";
import { isStoragePath, resolveAssetUrls, uploadMediaFile } from "@/lib/mediaStorage";
import { supabase } from "@/lib/supabase";
import { type GalleryItem, type MediaAsset } from "@/lib/adminContent";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  SearchInput,
  btnGhost,
  btnPrimary,
  textInput,
} from "@/pages/admin/components/AdminUI";

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (value: string, displayUrl: string) => void;
}

/**
 * Modal that browses existing image assets in the media library (resolving secure
 * links) and lets the user upload a brand-new image. Selecting or uploading one
 * returns both the stored value and a ready-to-render display URL.
 */
export function MediaPicker({ open, onClose, onSelect }: MediaPickerProps) {
  const [rows, setRows] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = await listRows<MediaAsset>("media_assets", "created_at");
      const images = all.filter((row) => row.asset_type === "image");
      setRows(images);
      const paths = images
        .filter((row) => isStoragePath(row.url))
        .map((row) => row.url as string);
      setSignedUrls(paths.length ? await resolveAssetUrls(paths) : {});
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load the media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const displayUrl = useCallback(
    (url: string | null): string | null => {
      if (!url) return null;
      return isStoragePath(url) ? signedUrls[url] || null : url;
    },
    [signedUrls],
  );

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { data } = await supabase.auth.getUser();
      const uploadedBy = data.user?.email || "Admin";
      const result = await uploadMediaFile(file);
      const created = await insertRow<MediaAsset>("media_assets", {
        file_name: result.fileName,
        title: file.name.replace(/\.[^.]+$/, ""),
        asset_type: "image",
        folder: "Uploads",
        url: result.path,
        size_kb: result.sizeKb,
        dimensions: result.dimensions,
        alt_text: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
        uploaded_by: uploadedBy,
        usage_count: 0,
        status: "active",
      });
      const signed = await resolveAssetUrls([result.path]);
      setRows((list) => [created, ...list]);
      setSignedUrls((map) => ({ ...map, ...signed }));
      onSelect(result.path, signed[result.path] || "");
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.title || "", row.file_name, row.alt_text || ""].join(" ").toLowerCase().includes(query),
    );
  }, [rows, search]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose a cover image"
      subtitle="Media Library"
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={btnPrimary}
          >
            {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
            Upload new image
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(event) => {
              handleFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </>
      }
    >
      <SearchInput value={search} onChange={setSearch} placeholder="Search images by name, title…" />

      {error ? <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} /> : null}

      {loading ? (
        <LoadingState label="Loading images…" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-image-2-line"
          title={rows.length === 0 ? "No images yet" : "No images match"}
          message="Upload a file to add your first image, then pick it here."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((asset) => {
            const url = displayUrl(asset.url);
            return (
              <button
                key={asset.id}
                type="button"
                disabled={!url}
                onClick={() => {
                  if (!url) return;
                  onSelect(asset.url as string, url);
                  onClose();
                }}
                className="text-left bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                <div className="w-full h-28 bg-background-100 flex items-center justify-center text-foreground-400">
                  {url ? (
                    <img src={url} alt={asset.alt_text || asset.file_name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <i className="ri-image-line text-2xl"></i>
                  )}
                </div>
                <p className="px-3 py-2 text-xs font-semibold text-foreground-800 truncate">
                  {asset.title || asset.file_name}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

interface CoverPickerProps {
  label: string;
  value: string;
  preview: string;
  onChange: (value: string, preview: string) => void;
}

/**
 * Cover-image field for content forms. Shows a live preview, allows pasting an
 * external URL, and opens the MediaPicker to choose from (or upload into) the
 * media library.
 */
export function CoverPicker({ label, value, preview, onChange }: CoverPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
        {label}
      </span>
      <div className="space-y-3">
        {preview ? (
          <div className="w-full h-40 rounded-lg overflow-hidden bg-background-100 border border-background-200">
            <img src={preview} alt="Cover preview" className="w-full h-full object-cover object-top" />
          </div>
        ) : (
          <div className="w-full h-28 rounded-lg bg-background-100 border border-dashed border-background-300 flex flex-col items-center justify-center text-foreground-500">
            <i className="ri-image-add-line text-2xl"></i>
            <span className="text-xs mt-1">No cover chosen</span>
          </div>
        )}

        <input
          value={value}
          onChange={(event) => {
            const next = event.target.value;
            onChange(next, /^https?:\/\//i.test(next) ? next : "");
          }}
          placeholder="Paste an image URL, or choose from the library"
          className={textInput}
        />

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setOpen(true)} className={btnGhost}>
            <i className="ri-image-2-line"></i> Choose from library
          </button>
          {value ? (
            <button type="button" onClick={() => onChange("", "")} className={btnGhost}>
              <i className="ri-close-line"></i> Clear
            </button>
          ) : null}
        </div>
      </div>

      <MediaPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={onChange}
      />
    </div>
  );
}

interface MediaMultiPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (items: GalleryItem[]) => void;
  initial: GalleryItem[];
}

/**
 * Modal for choosing several images at once. Browse the library, toggle multiple
 * images, upload fresh ones, then confirm the whole selection in one go.
 */
export function MediaMultiPicker({ open, onClose, onConfirm, initial }: MediaMultiPickerProps) {
  const [rows, setRows] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<string, GalleryItem>>({});
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const initialKey = useMemo(() => initial.map((item) => item.src).join("|"), [initial]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = await listRows<MediaAsset>("media_assets", "created_at");
      const images = all.filter((row) => row.asset_type === "image");
      setRows(images);
      const paths = images
        .filter((row) => isStoragePath(row.url))
        .map((row) => row.url as string);
      setSignedUrls(paths.length ? await resolveAssetUrls(paths) : {});
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load the media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const seed: Record<string, GalleryItem> = {};
    initial.forEach((item) => {
      seed[item.src] = item;
    });
    setSelected(seed);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKey, load]);

  const displayUrl = useCallback(
    (url: string | null): string | null => {
      if (!url) return null;
      return isStoragePath(url) ? signedUrls[url] || null : url;
    },
    [signedUrls],
  );

  const toggle = (asset: MediaAsset) => {
    const src = asset.url as string;
    setSelected((map) => {
      if (map[src]) {
        const next = { ...map };
        delete next[src];
        return next;
      }
      return {
        ...map,
        [src]: {
          src,
          alt: asset.alt_text || asset.title || asset.file_name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
        },
      };
    });
  };

  const handleFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      const { data } = await supabase.auth.getUser();
      const uploadedBy = data.user?.email || "Admin";
      for (const file of Array.from(files)) {
        const result = await uploadMediaFile(file);
        const created = await insertRow<MediaAsset>("media_assets", {
          file_name: result.fileName,
          title: file.name.replace(/\.[^.]+$/, ""),
          asset_type: "image",
          folder: "Uploads",
          url: result.path,
          size_kb: result.sizeKb,
          dimensions: result.dimensions,
          alt_text: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
          uploaded_by: uploadedBy,
          usage_count: 0,
          status: "active",
        });
        const signed = await resolveAssetUrls([result.path]);
        setRows((list) => [created, ...list]);
        setSignedUrls((map) => ({ ...map, ...signed }));
        setSelected((map) => ({
          ...map,
          [result.path]: { src: result.path, alt: created.alt_text || created.file_name },
        }));
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.title || "", row.file_name, row.alt_text || ""].join(" ").toLowerCase().includes(query),
    );
  }, [rows, search]);

  const selectedCount = Object.keys(selected).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose gallery photos"
      subtitle="Media Library"
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={btnGhost}
          >
            {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
            Upload new
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm(Object.values(selected));
              onClose();
            }}
            className={btnPrimary}
          >
            <i className="ri-check-line"></i> Add {selectedCount} photo{selectedCount === 1 ? "" : "s"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(event) => {
              handleFile(event.target.files);
              event.target.value = "";
            }}
          />
        </>
      }
    >
      <SearchInput value={search} onChange={setSearch} placeholder="Search images by name, title…" />

      {error ? <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} /> : null}

      {loading ? (
        <LoadingState label="Loading images…" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-image-2-line"
          title={rows.length === 0 ? "No images yet" : "No images match"}
          message="Upload a file to add your first image, then pick it here."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((asset) => {
            const url = displayUrl(asset.url);
            const isSelected = Boolean(asset.url && selected[asset.url]);
            return (
              <button
                key={asset.id}
                type="button"
                disabled={!url}
                onClick={() => toggle(asset)}
                className={`relative text-left bg-background-50 border rounded-lg overflow-hidden transition-colors cursor-pointer disabled:opacity-50 ${
                  isSelected ? "border-primary-500" : "border-background-200 hover:border-primary-300"
                }`}
              >
                <span
                  className={`absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center rounded-md border transition-colors ${
                    isSelected
                      ? "bg-primary-500 border-primary-500 text-background-50"
                      : "bg-background-50/90 border-background-300 text-foreground-500"
                  }`}
                >
                  <i className={isSelected ? "ri-checkbox-line" : "ri-checkbox-blank-line"}></i>
                </span>
                <div className="w-full h-28 bg-background-100 flex items-center justify-center text-foreground-400">
                  {url ? (
                    <img src={url} alt={asset.alt_text || asset.file_name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <i className="ri-image-line text-2xl"></i>
                  )}
                </div>
                <p className="px-3 py-2 text-xs font-semibold text-foreground-800 truncate">
                  {asset.title || asset.file_name}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

interface GalleryPickerProps {
  label: string;
  value: GalleryItem[];
  onChange: (items: GalleryItem[]) => void;
}

/**
 * Multi-image gallery field for content forms. Shows the chosen photos as a
 * reorderable strip and opens the multi-select picker to add or replace images.
 */
export function GalleryPicker({ label, value, onChange }: GalleryPickerProps) {
  const [open, setOpen] = useState(false);
  const urlMap = useAssetUrlMap(value.map((item) => item.src));

  const resolve = (src: string) => (isStoragePath(src) ? urlMap[src] : src);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
        {label}
      </span>

      {value.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          {value.map((item, index) => {
            const preview = resolve(item.src);
            return (
              <div key={`${item.src}-${index}`} className="group relative bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="w-full h-24 bg-background-100 flex items-center justify-center text-foreground-400">
                  {preview ? (
                    <img src={preview} alt={item.alt || "Gallery photo"} className="w-full h-full object-cover object-top" />
                  ) : (
                    <i className="ri-image-line text-2xl"></i>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label="Move earlier"
                      className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-xs"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === value.length - 1}
                      aria-label="Move later"
                      className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-xs"></i>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onChange(value.filter((_, i) => i !== index))}
                    aria-label="Remove photo"
                    className="w-6 h-6 flex items-center justify-center rounded-md text-accent-700 hover:bg-accent-100 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="w-full h-24 rounded-lg bg-background-100 border border-dashed border-background-300 flex flex-col items-center justify-center text-foreground-500 mb-3">
          <i className="ri-gallery-line text-2xl"></i>
          <span className="text-xs mt-1">No gallery photos yet</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setOpen(true)} className={btnGhost}>
          <i className="ri-image-add-line"></i> {value.length ? "Add / manage photos" : "Add photos"}
        </button>
        {value.length ? (
          <button type="button" onClick={() => onChange([])} className={btnGhost}>
            <i className="ri-close-line"></i> Clear all
          </button>
        ) : null}
      </div>

      <MediaMultiPicker
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onChange}
        initial={value}
      />
    </div>
  );
}